<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: How am I doing this month, and what changed?
  Answer:   the month-to-date headline with its named comparison window, then net worth
            with its same-point change and the full history beneath it.
  Context:  the cash-flow savings rate, category and account movement and recent transactions,
            each with its own caption.
  Controls: the refresh button.
-->
<script setup lang="ts">
import { ActionButton, ErrorState, Skeleton } from '@taler/ui'
import { daysBetween, formatLocalDate, plusMonths, startOfMonth, systemClock, todayUtc } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  AccountMovementList,
  canViewDashboard,
  CashFlowFigures,
  compareSamePointLastMonth,
  CategorySpendingList,
  HomePanel,
  MonthToDatePanel,
  NetWorthPanel,
  useAccountFlowSummaries,
  useCashFlow,
  useCategorySpending,
  useDashboard,
  useHomeAccounts,
  useNetWorthHistory,
} from '@/features/dashboard'
import { canViewTransactions, RecentTransactions, useTransactions } from '@/features/transactions'
import { useDisplayCurrency } from '@/app/display-currency'
import { useSession } from '@/app/session'

const { t, locale } = useI18n()
const session = useSession()
const { displayCurrency } = useDisplayCurrency()

const canView = computed(() => canViewDashboard(session.can))
const canViewNames = computed(() => session.can('ACCOUNT_VIEW_NAMES'))
const canViewAccountMovement = computed(() => session.can('ACCOUNT_VIEW_BALANCES') && canViewNames.value)
const canViewRecent = computed(() => canViewTransactions(session.can))

// Fixed for the page's lifetime: "today" is taken once on arrival, not as a query drifts past midnight.
const asOf = todayUtc(systemClock)
const from = startOfMonth(asOf)
const previousAsOf = plusMonths(asOf, -1)
const previousFrom = startOfMonth(previousAsOf)
const homeWindow = { from, until: asOf }
const homeWindowQuery = { from: formatLocalDate(from), until: formatLocalDate(asOf) }
const previousWindow = { from: previousFrom, until: previousAsOf }
const comparisonDays = daysBetween(previousAsOf, asOf)

const ledgerId = () => session.activeLedger.value?.id
const currency = () => displayCurrency.value
const enabled = () => canView.value

const dashboard = useDashboard({ ledgerId, displayCurrency: currency, enabled, asOf })

// The same point last month: a like-for-like month-to-date comparison. Its failure only
// withholds the comparison; the current figures still render.
const previousMonth = useDashboard({
  ledgerId,
  displayCurrency: currency,
  enabled,
  asOf: previousAsOf,
})

const netWorthHistory = useNetWorthHistory({ ledgerId, displayCurrency: currency, enabled })
const cashFlow = useCashFlow({ ledgerId, displayCurrency: currency, window: homeWindow, enabled })
const categories = useCategorySpending({ ledgerId, displayCurrency: currency, window: homeWindow, enabled })
const accountFlows = useAccountFlowSummaries({
  ledgerId,
  displayCurrency: currency,
  window: homeWindow,
  enabled: () => canView.value && canViewAccountMovement.value,
})
const accounts = useHomeAccounts({
  ledgerId,
  enabled: () => canView.value && canViewNames.value,
})
const recent = useTransactions({
  ledgerId,
  locale: () => locale.value as 'en' | 'hu',
  enabled: () => canView.value && canViewRecent.value,
  filters: () => ({}),
})

const monthComparison = computed(() =>
  dashboard.data.value && previousMonth.data.value
    ? compareSamePointLastMonth(dashboard.data.value, previousMonth.data.value)
    : undefined,
)

const moneyAccountFlows = computed(() =>
  (accountFlows.data.value ?? []).filter(
    (flow) => accounts.index.value.get(flow.accountId)?.isMoney === true,
  ),
)

function nameOf(accountId: string): string | undefined {
  return accounts.index.value.get(accountId)?.name
}

function retrySpending() {
  categories.retry()
  accounts.retry()
}

function retryAccountMovement() {
  accountFlows.retry()
  accounts.retry()
}

function refresh() {
  dashboard.retry()
  previousMonth.retry()
  netWorthHistory.retry()
  cashFlow.retry()
  categories.retry()
  accountFlows.retry()
  if (canViewNames.value) accounts.retry()
  recent.retry()
}
</script>

<template>
  <main class="mx-auto flex w-full max-w-[92.5rem] flex-col gap-6 p-[var(--space-page)]">
    <div class="flex flex-wrap items-end justify-between gap-[var(--space-group)]">
      <div class="flex min-w-0 flex-col gap-1">
        <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
          {{ t('dashboard.title') }}
        </h1>
        <p v-if="session.activeLedger.value" class="text-[color:var(--color-text-muted)]">
          {{ session.activeLedger.value.name }}
        </p>
      </div>
      <ActionButton v-if="canView" variant="secondary" @click="refresh">
        {{ t('dashboard.refresh') }}
      </ActionButton>
    </div>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('dashboard.noLedger') }}
    </p>

    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('dashboard.noAccess') }}
    </p>

    <template v-else-if="dashboard.isLoading.value">
      <div aria-hidden="true" class="flex flex-col gap-8">
        <Skeleton width="100%" height="9rem" />
        <Skeleton width="100%" height="16rem" />
      </div>
      <p class="sr-only" role="status">{{ t('dashboard.loading') }}</p>
    </template>

    <ErrorState
      v-else-if="dashboard.error.value"
      :title="t('dashboard.errorTitle')"
      :body="t('dashboard.errorBody')"
      :retry-label="t('dashboard.retry')"
      :retrying="dashboard.retrying.value"
      @retry="dashboard.retry()"
    />

    <template v-else-if="dashboard.data.value">
      <MonthToDatePanel
        :dashboard="dashboard.data.value"
        :comparison="monthComparison"
        :from="homeWindow.from"
        :until="homeWindow.until"
        :previous-from="previousWindow.from"
        :previous-until="previousWindow.until"
      />

      <NetWorthPanel
        :dashboard="dashboard.data.value"
        :previous-month="previousMonth.data.value"
        :net-worth-history="netWorthHistory.data.value"
      />

      <div class="grid grid-cols-1 gap-[var(--space-group)] xl:grid-cols-12">
        <HomePanel
          v-if="canViewNames"
          class="xl:col-span-8"
          :title="t('dashboard.spending')"
          :is-loading="categories.isLoading.value || accounts.isLoading.value"
          :error="categories.error.value ?? accounts.error.value"
          :retrying="categories.retrying.value || accounts.retrying.value"
          :view-all="{ name: 'reporting-categories', query: homeWindowQuery }"
          @retry="retrySpending"
        >
          <CategorySpendingList
            v-if="categories.data.value"
            :spending="categories.data.value"
            :name-of="nameOf"
            :window-days="comparisonDays"
          />
        </HomePanel>
        <HomePanel
          class="xl:col-span-4"
          :title="t('dashboard.cashFlow')"
          :is-loading="cashFlow.isLoading.value"
          :error="cashFlow.error.value"
          :retrying="cashFlow.retrying.value"
          @retry="cashFlow.retry()"
        >
          <CashFlowFigures v-if="cashFlow.data.value" :cash-flow="cashFlow.data.value" />
        </HomePanel>
      </div>

      <div class="grid grid-cols-1 gap-[var(--space-group)] xl:grid-cols-12">
        <HomePanel
          v-if="canViewAccountMovement"
          class="xl:col-span-7"
          :title="t('dashboard.accountMovement')"
          :is-loading="accountFlows.isLoading.value || accounts.isLoading.value"
          :error="accountFlows.error.value ?? accounts.error.value"
          :retrying="accountFlows.retrying.value || accounts.retrying.value"
          :view-all="{ name: 'reporting-accounts', query: homeWindowQuery }"
          @retry="retryAccountMovement"
        >
          <AccountMovementList v-if="accountFlows.data.value" :flows="moneyAccountFlows" :name-of="nameOf" />
        </HomePanel>
        <div
          v-if="canViewRecent"
          class="min-w-0 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-group)] xl:col-span-5"
        >
          <RecentTransactions
            :rows="recent.rows.value"
            :is-loading="recent.isLoading.value"
            :error="recent.error.value"
            :heading="t('dashboard.recentTransactions')"
          />
        </div>
      </div>
    </template>
  </main>
</template>
